/**
 * Service Breakdown Section Component
 * Displays service-level capacity breakdown with progress bars
 * Reusable across Day view and Week view modal
 */

import { useState } from 'react';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { translateService } from '@/lib/serviceTranslations';
import { useBillingSettings } from '@/hooks/useBilling';
import { useServiceBreakdown } from '@/hooks/useCapacity';
import { useCurrentCapacityThresholds } from '@/hooks/useCapacitySettings';
import { getCapacityColor } from '@/lib/capacityColor';
import { cn } from '@/lib/utils';
import { formatDecimal, formatPercent } from '@/lib/format';
import type { CapacityMetricMode } from '@/types/capacity';

interface ServiceBreakdownSectionProps {
  groupId: string;
  date: string;
  peakService?: string;
  /** Selected service code from the capacity switch; when != "PEAK" the header
   * shows that service's headcount/weighted instead of the peak's. */
  activeServiceView?: string;
  defaultExpanded?: boolean;
  /** Which metric reads as the primary header figure. The other stays visible. */
  metricMode?: CapacityMetricMode;
}

/** Header figure styling — the operative metric is a type-step larger.
 *  min-w-0: grid items default to min-width:auto (= min-content), so without it
 *  a long figure pushes past its track instead of clipping inside it. */
const PRIMARY_FIGURE_CLASS = 'text-sm font-semibold tabular-nums text-right min-w-0';
const SECONDARY_FIGURE_CLASS = 'text-xs text-muted-foreground tabular-nums text-right min-w-0';

/** Column template shared by the header row and the per-service rows. Both grids
 *  MUST resolve identical tracks or the header stops labelling the column beneath
 *  it; keeping ONE constant makes that drift impossible rather than merely tested. */
const BREAKDOWN_GRID_CLASS =
  'grid grid-cols-[minmax(120px,auto)_minmax(100px,auto)_1fr_minmax(200px,1fr)_minmax(200px,1fr)_24px] items-center gap-2';

export function ServiceBreakdownSection({
  groupId,
  date,
  peakService,
  activeServiceView = 'PEAK',
  defaultExpanded = false,
  metricMode = 'weighted',
}: ServiceBreakdownSectionProps) {
  const { i18n } = useLingui();
  const { data: billingSettings } = useBillingSettings();
  const services = billingSettings?.services || [];
  const { warningThreshold, criticalThreshold } = useCurrentCapacityThresholds();

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Whole percentages and one-decimal weighted figures in the UI locale.
  const pct = (value: number) => formatPercent(value / 100, i18n.locale, 0);
  const dec = (value: number) => formatDecimal(value, i18n.locale, 1);

  // Fetch service breakdown data
  const { data: serviceBreakdownData, isLoading: isServiceBreakdownLoading } = useServiceBreakdown(
    groupId,
    date,
    { enabled: true }
  );

  // The star badge always tracks the actual peak so the user keeps that context
  // even when filtering by another service.
  const effectivePeakService = peakService || serviceBreakdownData?.peak_service;

  // When a non-PEAK view is selected, the header shows that service's numbers
  // up-front; otherwise the peak's numbers drive the display.
  const focalService = activeServiceView !== 'PEAK' ? activeServiceView : effectivePeakService;

  // Compute service rows - use empty array if no data
  const serviceRows = serviceBreakdownData?.services ?? [];

  const isWeightedMetric = metricMode === 'weighted';

  // Progress-bar fill from the shared capacity traffic-light (theme-aware tokens).
  const getProgressColor = (percent: number | null | undefined): string =>
    getCapacityColor(percent, { warningThreshold, criticalThreshold }).bar;

  // Helper to render peak badge
  // #1156: the parameters used to shadow the outer bindings while typing
  // themselves as `typeof <same name>`, which is circular (TS2502). Every value
  // is already in scope here, so the badge closes over them directly.
  const renderPeakBadge = (effectivePeakService: string) => {
    if (isServiceBreakdownLoading) {
      return (
        <Badge variant="secondary" className="text-xs gap-1">
          <Star className="h-3 w-3 text-warning-on-surface fill-current flex-shrink-0 inline" />
          <Trans>Peak</Trans>: {translateService(effectivePeakService, services, i18n)} (...)
        </Badge>
      );
    }
    if (serviceBreakdownData?.services) {
      return (
        <Badge variant="secondary" className="text-xs whitespace-nowrap gap-1">
          <Star className="h-3 w-3 text-warning-on-surface fill-current flex-shrink-0 inline" />
          <Trans>Peak</Trans>: {translateService(effectivePeakService, services, i18n)}
        </Badge>
      );
    }
    return null;
  };

  // Utilization text colour from the same shared capacity traffic-light.
  const getUtilizationTextColor = (percent: number): string =>
    getCapacityColor(percent, { warningThreshold, criticalThreshold }).text;

  /**
   * Redundant (non-colour) status channel for a header figure: a lucide glyph
   * for warning/critical only — a normal figure stays quiet. Both fields are
   * nullable on the primitive, so both are guarded here.
   *
   * The label is sr-only: this dense header row has no width for it, and the
   * visible uppercase text overflowed the figure column in 5 of 6 locales.
   * Colour plus the distinct glyph shape carry the non-colour channel here.
   */
  const renderStatusChannel = (percent: number) => {
    const { icon: StatusIcon, label: statusLabel, isQuiet, text } = getCapacityColor(percent, {
      warningThreshold,
      criticalThreshold,
    });
    if (isQuiet || !StatusIcon || !statusLabel) return null;
    // Carries its own colour: the caller renders this inline next to a figure
    // whose own colour depends on whether it is the operative metric.
    return (
      <span className={cn('inline-flex items-center gap-1 ml-1', text)} data-testid="breakdown-status">
        <StatusIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="sr-only">{i18n._(statusLabel)}</span>
      </span>
    );
  };

  // Helper to get peak headcount display string and numeric percent
  const getPeakHeadcountInfo = (
    data: typeof serviceBreakdownData,
    effectivePeakService: string | undefined
  ): { display: string; percent: number } => {
    if (data?.services && effectivePeakService) {
      const peakServiceData = data.services.find(s => s.service_code === effectivePeakService);
      if (peakServiceData) {
        const headcountPercent = Math.round(peakServiceData.headcount_utilization_percent ?? 0);
        return { display: `${pct(headcountPercent)} (${peakServiceData.headcount ?? 0}/${peakServiceData.max_headcount ?? 0})`, percent: headcountPercent };
      }
    }
    if (data?.services && data.services.length > 0) {
      const firstService = data.services[0];
      const headcountPercent = Math.round(firstService.headcount_utilization_percent ?? 0);
      return { display: `${pct(headcountPercent)} (${firstService.headcount ?? 0}/${firstService.max_headcount ?? 0})`, percent: headcountPercent };
    }
    return { display: `${pct(0)} (0/0)`, percent: 0 };
  };

  // Helper to get peak weighted display string and numeric percent
  const getPeakWeightedInfo = (
    data: typeof serviceBreakdownData,
    effectivePeakService: string | undefined
  ): { display: string; percent: number } => {
    if (data?.services && effectivePeakService) {
      const peakServiceData = data.services.find(s => s.service_code === effectivePeakService);
      if (peakServiceData) {
        const weightedPercent = Math.round(peakServiceData.weighted_utilization_percent ?? 0);
        return { display: `${pct(weightedPercent)} (${dec(peakServiceData.weighted_headcount ?? 0)}/${dec(peakServiceData.max_weighted_headcount ?? 0)})`, percent: weightedPercent };
      }
    }
    if (data?.services && data.services.length > 0) {
      const firstService = data.services[0];
      const weightedPercent = Math.round(firstService.weighted_utilization_percent ?? 0);
      return { display: `${pct(weightedPercent)} (${dec(firstService.weighted_headcount ?? 0)}/${dec(firstService.max_weighted_headcount ?? 0)})`, percent: weightedPercent };
    }
    return { display: `${pct(0)} (${dec(0)}/${dec(0)})`, percent: 0 };
  }

  // Only hide if we definitively know there's no section to show
  // Keep the component visible during loading and when data might be available
  if (!isServiceBreakdownLoading && serviceBreakdownData && serviceRows.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full h-8 px-2">
            {/* Header row — shares BREAKDOWN_GRID_CLASS with the service rows below. */}
            <div className={`w-full ${BREAKDOWN_GRID_CLASS}`} data-testid="breakdown-header-grid">
              {/* Services label */}
              <span className="text-sm text-left">
                {isExpanded ? (
                  <Trans>Hide Services</Trans>
                ) : (
                  <Trans>Show Services</Trans>
                )}
              </span>

              {/* Spacer for time column */}
              <div />

              {/* Peak badge in spacer area - shows the actual peak (still useful
                  context even when the user is filtering by another service). */}
              <div className="flex justify-end">
                {effectivePeakService && renderPeakBadge(effectivePeakService)}
              </div>

              {/* Headcount — reflects the focal service (peak by default,
                  or the selected service when filtering). Primary in raw mode. */}
              {(() => {
                const info = getPeakHeadcountInfo(serviceBreakdownData, focalService);
                return (
                  <span
                    className={isWeightedMetric ? SECONDARY_FIGURE_CLASS : PRIMARY_FIGURE_CLASS}
                    data-testid="breakdown-headcount"
                  >
                    <Trans>Headcount</Trans>{' '}
                    <span className={isWeightedMetric ? undefined : getUtilizationTextColor(info.percent)}>
                      {info.display}
                    </span>
                    {renderStatusChannel(info.percent)}
                  </span>
                );
              })()}

              {/* Weighted — reflects the focal service (peak by default,
                  or the selected service when filtering). Primary by default (D6). */}
              {(() => {
                const info = getPeakWeightedInfo(serviceBreakdownData, focalService);
                return (
                  <span
                    className={isWeightedMetric ? PRIMARY_FIGURE_CLASS : SECONDARY_FIGURE_CLASS}
                    data-testid="breakdown-weighted"
                  >
                    <Trans>Weighted</Trans>{' '}
                    <span className={isWeightedMetric ? getUtilizationTextColor(info.percent) : undefined}>
                      {info.display}
                    </span>
                    {renderStatusChannel(info.percent)}
                  </span>
                );
              })()}

              {/* Collapse icon */}
              <div className="flex items-center justify-center">
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                )}
              </div>
            </div>
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2 space-y-2">
          {isServiceBreakdownLoading ? (
            <div className="text-sm text-muted-foreground text-center py-2">
              {i18n._(msg({ id: 'capacity.service.breakdown.loading', message: 'Loading service breakdown...' }))}
            </div>
          ) : serviceRows.length > 0 ? (
            serviceRows.map((service) => {
              const isPeakService = service.service_code === effectivePeakService;

              const headcountPercent = service.headcount_utilization_percent ?? 0;
              const weightedPercent = service.weighted_utilization_percent ?? 0;
              const maxPercent = Math.max(headcountPercent, weightedPercent);

              return (
                <div
                  key={service.service_code}
                  className={`relative rounded-lg border overflow-hidden min-h-[40px] ${isPeakService ? 'border-warning-on-surface/40' : 'border-border'}`}
                >
                  {/* Background progress fill */}
                  <div
                    className={`absolute inset-0 ${getProgressColor(maxPercent)} opacity-30`}
                    style={{ width: `${Math.min(maxPercent, 100)}%` }}
                  />

                  {/* Content on top of background - grid layout */}
                  <div className={`relative p-2 ${BREAKDOWN_GRID_CLASS}`} data-testid="breakdown-service-row-grid">
                    {/* Service name */}
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium whitespace-nowrap">
                        {translateService(service.service_code || '', services, i18n)}
                      </span>
                      {isPeakService && (
                        <Star className="h-3 w-3 text-warning-on-surface fill-current flex-shrink-0">
                          {/* #1156: an SVG element has no `title` ATTRIBUTE — React
                              emitted it but no browser renders a tooltip from it.
                              `<title>` as the first child is the SVG-native form and
                              is what screen readers announce. */}
                          <title>
                            {i18n._(msg({ id: 'capacity.peak.tooltip', message: 'Peak service for this day' }))}
                          </title>
                        </Star>
                      )}
                    </div>

                    {/* Time range */}
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {service.time_range}
                    </span>

                    {/* Spacer */}
                    <div />

                    {/* Headcount - no label, just values, no padding */}
                    <span className="text-xs font-medium whitespace-nowrap text-right">
                      {pct(Math.round(headcountPercent))} ({service.headcount ?? 0}/{service.max_headcount ?? 0})
                    </span>

                    {/* Weighted - no label, just values, no padding */}
                    <span className="text-xs font-medium whitespace-nowrap text-right">
                      {pct(Math.round(weightedPercent))} ({dec(service.weighted_headcount ?? 0)}/{dec(service.max_weighted_headcount ?? 0)})
                    </span>

                    {/* Space for collapse icon */}
                    <div className="w-6" />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-sm text-muted-foreground text-center py-2">
              {i18n._(msg({ id: 'capacity.service.breakdown.empty', message: 'No service breakdown available' }))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
  );
}

export default ServiceBreakdownSection;
